import { Box } from '@hivebrite/design-system/Box';
import { Button } from '@hivebrite/design-system/Button';
import { CardWithTitle } from '@hivebrite/design-system/Card';
import { Link } from '@hivebrite/design-system/Link';
import { UserPresentation } from '@hivebrite/design-system/UserPresentation';
import { useToggle } from '@hivebrite/react-hooks';

import ModalWithTemplate from 'hb-react/shared/components/DesignSystem/Modal/ModalWithTemplate';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { completeName } from 'helpers/name';

type Users = Array<{
  id: number;
  photo_thumb_url: string;
  photo_medium_url: string;
  description: string;
  new_photo: {
    thumbnail_url: string;
  };
}>;

interface ExpertHomeGeneralInfosProps {
  numberOfUsersDisplayed: number;
  title: string;
  users: Users;
}

const UserListDisplay = ({ users }: { users: Users }) => {
  const { flagEvUserPicturesToGlobalStorage } = useFeatureFlag();

  return users.map((user, index) => (
    <Box
      data-testid="group-live-feed-users-list"
      width="100%"
      mb={4}
      pr={index % 2 ? 0 : { xl: 6 }}
      key={user.id}
    >
      <UserPresentation
        avatar={
          flagEvUserPicturesToGlobalStorage
            ? user.new_photo?.thumbnail_url
            : user.photo_thumb_url || user.photo_medium_url
        }
        headline={user.description}
        name={
          <Link
            styledOnlyOnHover
            to={Routes.user_path(user.id)}
            font="body-semibold-asphalt"
          >
            {completeName(user)}
          </Link>
        }
        path={Routes.user_path(user.id)}
      />
    </Box>
  ));
};

const ExpertHomeGeneralInfos = ({
  numberOfUsersDisplayed,
  title,
  users = [],
}: ExpertHomeGeneralInfosProps) => {
  const { open, close, isOpen } = useToggle();

  return (
    <CardWithTitle
      data-testid="recently-active-members"
      title={title}
      mb={6}
      pt={4}
      px={6}
    >
      <UserListDisplay users={users.slice(0, 3)} />

      {numberOfUsersDisplayed > 3 && (
        <Box justifyContent="center" mt={4}>
          <Button onClick={open}>{__('See more')}</Button>
        </Box>
      )}

      {isOpen && (
        <ModalWithTemplate
          onCloseModal={close}
          header={title}
          dataTestId="expert-home-general-infos-modal"
        >
          <Box display="flex" flexWrap="wrap">
            <UserListDisplay users={users} />
          </Box>
        </ModalWithTemplate>
      )}
    </CardWithTitle>
  );
};

export default ExpertHomeGeneralInfos;
